import { compact } from 'lodash-es';

import { AclosetMessageError } from '@looko-corp/acloset-react-kit';

import type { AclosetPlanActionCode, AclosetServicePricingCodeCode } from '@/MetadataType';
import type { DomainAvailableFeatureResponse, RequestParams } from '@/Swagger';
import { aimsApi } from '@/axios/AclosetApi';
import { useSuspenseSwaggerQuery0, useSwaggerQuery0, useSwaggerQuery1 } from '@/axios/hooks/useSwaggerQuery';

export interface DomainPlanExt {
  /**
   * 사용 가능한 Feature 셋.
   */
  readonly availableFeaturesSet: Set<string>;

  availableFeatures: Array<DomainAvailableFeatureResponse>;

  /**
   * feature 사용 가능 여부.
   */
  enabled(code: AclosetPlanActionCode): boolean;

  availableServiceSet: Set<string>;
  /**
   * service 사용 가능 여부.
   */
  enabledService(code: AclosetServicePricingCodeCode): boolean;

  lastServiceChangeDate?: string;
}

declare module '@/Swagger' {
  interface DomainGetMyServicePricingsResponse {
    extendedPlan: DomainPlanExt;
  }
}

export const planKeys = {
  list: ['plans'] as const,
  all: ['plan'] as const,
  detail: (shopID: string) => [...planKeys.all, shopID] as const,
  status: (shopID: string) => [...planKeys.detail(shopID), 'status'] as const,
} as const;

/**
 * 해당 플랜은 샵과 상관없는 플랜의 프리셋 속성을 가지고 있습니다.
 */
export const usePlansList = () => useSwaggerQuery0(aimsApi.shops.plansList, planKeys.list);

/**
 * usePlansList suspense.
 */
export const useSuspensePlansList = () => useSuspenseSwaggerQuery0(aimsApi.shops.plansList, planKeys.list);

/**
 * 샵에 적용이 되어있는 플랜을 단건 조회합니다.
 */
export const useShopPlansDetail = (shopID: string) =>
  useSwaggerQuery1(
    async (shopId: string, params: RequestParams = {}): ReturnType<typeof aimsApi.shops.servicePricingsDetail> => {
      if (!shopId) {
        throw new AclosetMessageError('Empty shop ID');
      }

      return aimsApi.shops.servicePricingsDetail(shopId, params).then(v => {
        v.data.extendedPlan = {
          availableFeaturesSet: new Set(
            compact(v.data.servicePricings?.flatMap(v => v.availableFeatures.map(v => v.code))),
          ),

          availableServiceSet: new Set(compact(v.data.servicePricings?.flatMap(v => v.code))),
          availableFeatures: v.data.servicePricings?.flatMap(v => v.availableFeatures) ?? [],

          enabled(code) {
            return this.availableFeaturesSet.has(code);
          },

          enabledService(code) {
            return this.availableServiceSet.has(code);
          },

          lastServiceChangeDate: v.data.lastServiceChangeDate,
        };

        return v;
      });
    },
    planKeys.detail,
    shopID,
    {
      enabled: !!shopID,
    },
  );

/**
 * 플랜과 관계된 현재 상태를 조회합니다.
 */
export const useShopPlanStatus = (shopID: string) =>
  useSwaggerQuery1(aimsApi.shops.plansStatusDetail, planKeys.status, shopID, {
    enabled: !!shopID,
  });
